import React, { useCallback, useEffect, useState } from "react";
import { Form, Modal } from "react-bootstrap";
import Cropper from "react-easy-crop";

// Longest side of the exported image; keeps uploads light without visible quality loss
const MAX_OUTPUT_SIZE = 1200;
const MAX_ZOOM = 3;
// Fills any part of the frame the photo doesn't cover (matches the client About page background)
const FILL_COLOR = "#ffffff";

// Frame options; the values match `imageShape` on the admin record and the client About page
export const IMAGE_SHAPES = {
  rectangle: { label: "Rectangle", aspect: 4 / 5, cropShape: "rect" },
  circle: { label: "Circle", aspect: 1, cropShape: "round" },
};
const DEFAULT_SHAPE = "rectangle";

// Zoom at which the whole photo fits inside the crop frame (1 = photo covers the frame)
const getFitZoom = (mediaSize, aspect) => {
  const ratio = mediaSize.naturalWidth / mediaSize.naturalHeight;
  return Math.min(1, aspect / ratio, ratio / aspect);
};

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  });

// Draws the selected area onto a canvas and returns it as an image Blob.
// Circles are exported as a square image; the round mask is applied where it is displayed.
const getCroppedBlob = async (imageSrc, pixelCrop, mimeType) => {
  const image = await loadImage(imageSrc);
  const scale = Math.min(1, MAX_OUTPUT_SIZE / Math.max(pixelCrop.width, pixelCrop.height));

  const canvas = document.createElement("canvas");
  canvas.width = Math.round(pixelCrop.width * scale);
  canvas.height = Math.round(pixelCrop.height * scale);

  const ctx = canvas.getContext("2d");
  ctx.fillStyle = FILL_COLOR;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.imageSmoothingQuality = "high";
  // Draw the whole image offset by the crop position; this also works when the
  // frame extends past the photo's edges (zoomed out), where the fill shows through
  ctx.drawImage(
    image,
    -pixelCrop.x * scale,
    -pixelCrop.y * scale,
    image.naturalWidth * scale,
    image.naturalHeight * scale
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Could not crop image"))),
      mimeType,
      0.92
    );
  });
};

/**
 * Lets the admin pick a frame shape, then drag/zoom a photo inside it before it is uploaded.
 * The photo opens fully visible, so no part of it is cut off until the admin zooms in.
 *
 * Props:
 *  - show, imageSrc (object/data URL), mimeType (original file type)
 *  - shape: initially selected key of IMAGE_SHAPES (default "rectangle"), title
 *  - onCancel()
 *  - onConfirm(blob, mimeType, shape) — may return a promise; the modal shows a saving state until it settles
 */
export default function ImageCropModal({
  show,
  imageSrc,
  mimeType,
  shape: initialShape = DEFAULT_SHAPE,
  title = "Adjust Picture",
  onCancel,
  onConfirm,
}) {
  const [shape, setShape] = useState(DEFAULT_SHAPE);
  const [mediaSize, setMediaSize] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [minZoom, setMinZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [saving, setSaving] = useState(false);

  const { aspect, cropShape } = IMAGE_SHAPES[shape];

  // Reset for every new image; the zoom is set to "whole photo" once it has loaded
  useEffect(() => {
    if (show) {
      setShape(IMAGE_SHAPES[initialShape] ? initialShape : DEFAULT_SHAPE);
      setMediaSize(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setMinZoom(1);
      setCroppedAreaPixels(null);
      setSaving(false);
    }
  }, [show, imageSrc, initialShape]);

  // Open (and re-open after a shape change) with the entire photo visible inside the frame
  useEffect(() => {
    if (!mediaSize) return;
    const fitZoom = getFitZoom(mediaSize, aspect);
    setMinZoom(fitZoom);
    setZoom(fitZoom);
    setCrop({ x: 0, y: 0 });
  }, [mediaSize, aspect]);

  const handleCropComplete = useCallback((_, areaPixels) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  // PNG keeps its format; everything else (JPG, WEBP, GIF) is exported as JPEG
  const outputType = mimeType === "image/png" ? "image/png" : "image/jpeg";

  const handleConfirm = async () => {
    if (!croppedAreaPixels || saving) return;
    setSaving(true);
    try {
      const blob = await getCroppedBlob(imageSrc, croppedAreaPixels, outputType);
      await onConfirm(blob, outputType, shape);
    } finally {
      setSaving(false);
    }
  };

  const handleHide = () => {
    if (!saving) onCancel();
  };

  return (
    <Modal
      show={show}
      onHide={handleHide}
      centered
      backdrop="static"
      className="image-crop-modal"
      backdropClassName="image-crop-backdrop"
    >
      <Modal.Header className="border-none justify-content-center">
        <Modal.Title className="mt-3">{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="pb-0">
        <div className="image-crop-shapes" role="group" aria-label="Picture shape">
          {Object.entries(IMAGE_SHAPES).map(([key, option]) => (
            <button
              key={key}
              type="button"
              className={`image-crop-shape-btn ${shape === key ? "active" : ""}`}
              onClick={() => setShape(key)}
              disabled={saving}
            >
              <span className={`image-crop-shape-icon ${key}`} />
              {option.label}
            </button>
          ))}
        </div>
        <p className="image-crop-hint">
          Drag to reposition and use the slider to zoom. The area inside the frame is how the
          picture will appear on the client About page.
        </p>
        <div className="image-crop-area">
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              minZoom={minZoom}
              maxZoom={MAX_ZOOM}
              aspect={aspect}
              cropShape={cropShape}
              objectFit="contain"
              showGrid={cropShape === "rect"}
              restrictPosition={false}
              onMediaLoaded={setMediaSize}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={handleCropComplete}
            />
          )}
        </div>
        <div className="image-crop-zoom">
          <span>Zoom</span>
          <Form.Range
            min={minZoom}
            max={MAX_ZOOM}
            step={0.01}
            value={zoom}
            disabled={saving}
            onChange={(e) => setZoom(Number(e.target.value))}
          />
        </div>
      </Modal.Body>
      <Modal.Footer className="border-none justify-content-center">
        <button className="secondary" type="button" onClick={handleHide} disabled={saving}>
          Cancel
        </button>
        <button
          className="primary"
          type="button"
          onClick={handleConfirm}
          disabled={!croppedAreaPixels || saving}
        >
          {saving ? "Saving..." : "Apply"}
        </button>
      </Modal.Footer>
    </Modal>
  );
}
